import { api } from './apiClient';

/**
 * CRM Tier 1-4 expansion service (migration 060 + related backend).
 * Territories & performance, tasks & calendar, attachments, campaigns,
 * forecast submissions, customer health-trend / rail / meeting-brief, quotation
 * intelligence, and AI narration. apiClient auto-unwraps the {status,data}
 * envelope; most endpoints nest one more named key, unwrapped by hand below.
 *
 * Rows off raw SELECTs are snake_case; hand-built aggregates are camelCase —
 * this mirrors the split documented in crmService.ts.
 */

// ---------------- Territories & performance ----------------
export interface Territory {
  id: number;
  name: string;
  region: string | null;
  states: string | null;
  ownerId: number | null;
  ownerName: string | null;
  targetAmount: number | null;
  isActive: number | boolean;
  customerCount?: number;
  openPipelineValue?: number;
  weightedPipelineValue?: number;
  revenueThisFY?: number;
  attainmentPct?: number | null;
}
export interface CreateTerritoryInput {
  name: string;
  region?: string;
  states?: string;
  ownerId?: number | null;
  targetAmount?: number | null;
}
export interface LeaderboardRow {
  userId: number;
  fullName: string;
  wonValue: number;
  wonCount: number;
  winRatePct: number | null;
  openPipeline: number;
  weightedPipeline: number;
  quotaAttainmentPct: number | null;
  avgDealSize: number | null;
}

// ---------------- Tasks & calendar ----------------
export const TASK_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const TASK_STATUSES = ['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];
export const TASK_ENTITY_TYPES = ['lead', 'opportunity', 'customer', 'none'] as const;
export type TaskEntityType = (typeof TASK_ENTITY_TYPES)[number];

export interface CrmTask {
  id: number;
  title: string;
  description: string | null;
  related_entity_type: TaskEntityType;
  related_entity_id: number | null;
  related_entity_label?: string | null;
  owner_id: number | null;
  owner_name?: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  due_at: string | null;
  reminder_at: string | null;
  completed_at: string | null;
  link?: string | null;
  created_at: string;
}
export interface CreateTaskInput {
  title: string;
  description?: string;
  relatedEntityType?: TaskEntityType;
  relatedEntityId?: number | null;
  ownerId?: number | null;
  priority?: TaskPriority;
  dueAt?: string | null;
  reminderAt?: string | null;
}
export interface TaskFilters {
  status?: TaskStatus;
  owner?: number;
  priority?: TaskPriority;
  relatedEntityType?: TaskEntityType;
  relatedEntityId?: number;
  dueFrom?: string;
  dueTo?: string;
  overdue?: boolean;
  page?: number;
  limit?: number;
}
export interface CalendarEvent {
  id: number | string;
  source: 'task' | 'activity';
  kind: string;
  title: string;
  at: string;
  entityType?: string | null;
  entityId?: number | null;
  status?: string | null;
  priority?: string | null;
  link?: string | null;
}

// ---------------- Attachments ----------------
export interface CrmAttachment {
  id: number;
  entity_type: 'lead' | 'opportunity' | 'customer';
  entity_id: number;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  url: string | null;
  doc_category: string | null;
  notes: string | null;
  uploaded_by_name?: string | null;
  created_at: string;
}
export interface CreateAttachmentInput {
  fileName: string;
  fileType?: string;
  fileSize?: number;
  url?: string;
  docCategory?: string;
  notes?: string;
}

// ---------------- Campaigns ----------------
export interface Campaign {
  id: number;
  name: string;
  type: string | null;
  channel: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  targetLeads: number | null;
  description?: string | null;
  ownerId: number | null;
  ownerName: string | null;
  leadsGenerated: number;
  qualifiedLeads: number;
  convertedLeads: number;
  pipelineValue: number;
  wonValue: number;
  costPerLead: number | null;
  roiPct: number | null;
}
export interface CreateCampaignInput {
  name: string;
  type?: string;
  channel?: string;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
  budget?: number | null;
  targetLeads?: number | null;
  description?: string;
  ownerId?: number | null;
}
export interface SourceRoiRow {
  source: string;
  leadCount: number;
  qualifiedCount: number;
  convertedCount: number;
  conversionRatePct: number | null;
  pipelineValue: number;
  wonValue: number;
}

// ---------------- Forecast submissions ----------------
export interface ForecastSubmission {
  id: number | null;
  userId: number;
  userName?: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  commitAmount: number;
  bestCaseAmount: number;
  pipelineAmount: number;
  notes?: string | null;
  submittedAt?: string | null;
  isDraft?: boolean;
}
export interface ForecastAccuracyRow {
  submissionId: number;
  userId: number;
  userName: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  commitAmount: number;
  bestCaseAmount: number;
  actualWon: number;
  accuracyPct: number | null;
}

// =============================================
// SERVICE
// =============================================
export const crmPlusService = {
  // ---- Territories & performance ----
  getTerritories: (): Promise<{ territories: Territory[]; count: number }> =>
    api.get('/crm-performance/territories'),
  getTerritory: (id: number): Promise<{ territory: any }> =>
    api.get(`/crm-performance/territories/${id}`),
  createTerritory: (data: CreateTerritoryInput): Promise<{ territory: any }> =>
    api.post('/crm-performance/territories', data),
  updateTerritory: (id: number, data: Partial<CreateTerritoryInput>): Promise<{ territory: any }> =>
    api.put(`/crm-performance/territories/${id}`, data),
  deleteTerritory: (id: number): Promise<void> =>
    api.delete(`/crm-performance/territories/${id}`).then(() => undefined),
  getLeaderboard: (): Promise<{ period: { from: string; to: string }; leaderboard: LeaderboardRow[] }> =>
    api.get('/crm-performance/leaderboard'),
  getWinLoss: (params?: { from?: string; to?: string }): Promise<any> =>
    api.get('/crm-performance/win-loss', { params }),

  // ---- Tasks & calendar ----
  getTasks: (filters: TaskFilters = {}): Promise<{ tasks: CrmTask[]; pagination: any }> =>
    api.get('/crm-tasks', { params: filters }),
  getMyTasks: (): Promise<{
    overdue: { count: number; tasks: CrmTask[] };
    dueToday: { count: number; tasks: CrmTask[] };
    upcoming: { count: number; tasks: CrmTask[] };
    done: { count: number; tasks: CrmTask[] };
  }> => api.get('/crm-tasks/my'),
  getTaskCalendar: (from?: string, to?: string): Promise<{ events: CalendarEvent[]; count: number }> =>
    api.get('/crm-tasks/calendar', { params: { from, to } }),
  getTaskStats: (): Promise<{ open: number; overdue: number; doneThisWeek: number }> =>
    api.get('/crm-tasks/stats'),
  createTask: (data: CreateTaskInput): Promise<CrmTask> =>
    api.post<{ task: CrmTask }>('/crm-tasks', data).then((r) => r.task),
  getTask: (id: number): Promise<CrmTask> =>
    api.get<{ task: CrmTask }>(`/crm-tasks/${id}`).then((r) => r.task),
  updateTask: (id: number, data: Partial<CreateTaskInput>): Promise<CrmTask> =>
    api.put<{ task: CrmTask }>(`/crm-tasks/${id}`, data).then((r) => r.task),
  setTaskStatus: (id: number, status: TaskStatus): Promise<CrmTask> =>
    api.patch<{ task: CrmTask }>(`/crm-tasks/${id}/status`, { status }).then((r) => r.task),
  deleteTask: (id: number): Promise<void> =>
    api.delete(`/crm-tasks/${id}`).then(() => undefined),

  // ---- Attachments ----
  getAttachments: (
    entityType: string,
    entityId: number
  ): Promise<{ attachments: CrmAttachment[]; count: number }> =>
    api.get(`/crm-attachments/${entityType}/${entityId}`),
  createAttachment: (
    entityType: string,
    entityId: number,
    data: CreateAttachmentInput
  ): Promise<CrmAttachment> =>
    api.post<{ attachment: CrmAttachment }>(`/crm-attachments/${entityType}/${entityId}`, data).then((r) => r.attachment),
  updateAttachment: (id: number, data: { docCategory?: string; notes?: string }): Promise<CrmAttachment> =>
    api.put<{ attachment: CrmAttachment }>(`/crm-attachments/${id}`, data).then((r) => r.attachment),
  deleteAttachment: (id: number): Promise<void> =>
    api.delete(`/crm-attachments/${id}`).then(() => undefined),

  // ---- Campaigns ----
  getCampaigns: (params?: { status?: string; type?: string; channel?: string; search?: string }): Promise<{ campaigns: Campaign[]; count: number }> =>
    api.get('/campaigns', { params }),
  getCampaign: (id: number): Promise<any> => api.get(`/campaigns/${id}`),
  createCampaign: (data: CreateCampaignInput): Promise<{ campaign: any }> => api.post('/campaigns', data),
  updateCampaign: (id: number, data: Partial<CreateCampaignInput>): Promise<{ campaign: any }> =>
    api.put(`/campaigns/${id}`, data),
  deleteCampaign: (id: number): Promise<void> => api.delete(`/campaigns/${id}`).then(() => undefined),
  getSourceRoi: (): Promise<{ sources: SourceRoiRow[] } | SourceRoiRow[]> => api.get('/campaigns/source-roi'),

  // ---- Forecast submissions ----
  getForecastSubmissions: (params?: { periodType?: string; periodStart?: string; userId?: number }): Promise<{ submissions: ForecastSubmission[]; count?: number }> =>
    api.get('/forecast-submissions', { params }),
  getMyForecast: (): Promise<ForecastSubmission> =>
    api.get<any>('/forecast-submissions/mine').then((r) => (r && r.submission ? r.submission : r)),
  upsertForecast: (data: Partial<ForecastSubmission>): Promise<{ submission: ForecastSubmission }> =>
    api.post('/forecast-submissions', data),
  getForecastAccuracy: (params?: { periodType?: string; userId?: number }): Promise<{ accuracy: ForecastAccuracyRow[] } | ForecastAccuracyRow[]> =>
    api.get('/forecast-submissions/accuracy', { params }),

  // ---- Customer health-trend / rail / meeting-brief ----
  getHealthTrend: (customerId: number): Promise<{ months: any[]; current: { score: number; band: string } }> =>
    api.get(`/crm/customer/${customerId}/health-trend`),
  getCustomerRail: (customerId: number): Promise<any> => api.get(`/crm/customer/${customerId}/rail`),
  getMeetingBrief: (customerId: number): Promise<any> => api.get(`/crm/customer/${customerId}/meeting-brief`),

  // ---- Quotation intelligence ----
  createQuotationFromOpportunity: (opportunityId: number): Promise<{ quotation: any }> =>
    api.post(`/sales/opportunities/${opportunityId}/create-quotation`, {}),
  getOpportunityQuoteInfo: (opportunityId: number): Promise<any> =>
    api.get(`/sales/opportunities/${opportunityId}/quote-info`),

  // ---- AI narration (degrades to { available:false } with no key) ----
  getAiStatus: (): Promise<{ available: boolean }> => api.get('/crm-ai/status'),
  aiCustomerSummary: (customerId: number): Promise<{ available: boolean; text?: string; model?: string; reason?: string }> =>
    api.post(`/crm-ai/customer/${customerId}/summary`, {}),
  aiMeetingBrief: (customerId: number): Promise<{ available: boolean; text?: string; model?: string; reason?: string }> =>
    api.post(`/crm-ai/customer/${customerId}/meeting-brief`, {}),
  aiOpportunitySummary: (opportunityId: number): Promise<{ available: boolean; text?: string; model?: string; reason?: string }> =>
    api.post(`/crm-ai/opportunity/${opportunityId}/summary`, {}),
  aiDraftFollowUp: (opportunityId: number, tone?: 'formal' | 'friendly' | 'urgent'): Promise<{ available: boolean; text?: string; model?: string; reason?: string }> =>
    api.post(`/crm-ai/opportunity/${opportunityId}/draft-followup`, { tone }),

  // ---- Reminder mailer ----
  reminderStatus: (): Promise<{ mailerEnabled: boolean; smtpConfigured: boolean; intervalMinutes: number; lastSweepAt: string | null }> =>
    api.get('/reminders/status'),
  reminderPreview: (): Promise<{ hasContent: boolean; total: number; counts: Record<string, number>; sections: Array<{ key: string; title: string; tone: string; items: Array<{ label: string; meta: string; link: string }> }> }> =>
    api.get('/reminders/preview'),
  reminderSendNow: (): Promise<{ sent: boolean; reason: string; digest: any }> =>
    api.post('/reminders/send-now', {}),
};

export default crmPlusService;
